'use client'
import Image from 'next/image'
import { cn } from '@/utils/twMerge'
import { createPortal } from 'react-dom'
import * as prismic from '@prismicio/client'
import { PrismicNextImage } from '@prismicio/next'
import { PrismicLink, PrismicRichText } from '@prismicio/react'
import { useCallback, useEffect, useId, useRef, useState } from 'react'
import {
  findSpeakerForSummitLine,
  normalizeSummitSpeakerLine,
  splitSummitSessionSpeakerLine,
  type SummitSpeakerDirectoryItem,
} from '@/utils/summitSessionSpeakerUtils'

import { DAY_ACCENTS, type AccentKey } from './summitSessionConstants'

type SessionItem = Record<string, any>

function getYouTubeEmbedUrl(field: prismic.LinkField | null | undefined): string | null {
  if (!prismic.isFilled.link(field)) return null
  const url = 'url' in field ? field.url : undefined
  if (!url) return null

  try {
    const parsed = new URL(url)
    const host = parsed.hostname.replace(/^www\./, '')
    let videoId: string | null = null

    if (host === 'youtu.be') {
      videoId = parsed.pathname.slice(1)
    } else if (host === 'youtube.com' || host === 'm.youtube.com') {
      videoId = parsed.pathname.startsWith('/embed/') ? parsed.pathname.split('/embed/')[1] ?? null : parsed.searchParams.get('v')
    }

    return videoId ? `https://www.youtube.com/embed/${videoId}` : null
  } catch {
    return null
  }
}

const TABBABLE_SELECTOR =
  'a[href]:not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"]), input:not([disabled]):not([type="hidden"]):not([tabindex="-1"]), select:not([disabled]):not([tabindex="-1"]), textarea:not([disabled]):not([tabindex="-1"]), iframe:not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])'

function getKeyboardFocusableElements(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(TABBABLE_SELECTOR)).filter((el) => {
    if (el.getAttribute('aria-hidden') === 'true') return false
    const style = window.getComputedStyle(el)
    if (style.display === 'none' || style.visibility === 'hidden') return false
    const rects = el.getClientRects()
    return rects.length > 0 && (rects[0]?.width ?? 0) > 0 && (rects[0]?.height ?? 0) > 0
  })
}

function sessionSpeakerLines(field: prismic.RichTextField | null | undefined): string[] {
  if (!prismic.isFilled.richText(field)) return []
  const lines: string[] = []
  for (const block of field) {
    if (block.type === 'list-item' || block.type === 'paragraph') {
      const line = prismic.asText([block]).trim()
      if (line) lines.push(line)
    }
  }
  return lines
}

function SessionSpeakerLine({ text }: { text: string }) {
  const parsed = splitSummitSessionSpeakerLine(text)
  const bodyClass = 'text-sm font-normal leading-[140%] text-primary-ink-85 [font-weight:400] [&_strong]:font-normal'

  if (!parsed) {
    return <p className={bodyClass}>{normalizeSummitSpeakerLine(text)}</p>
  }

  return (
    <p className={`flex items-start gap-x-1 ${bodyClass}`}>
      <span className="shrink-0 pt-[0.02em]">{parsed.name}</span>
      <span className="shrink-0 pt-[0.02em] text-primary-ink-85" aria-hidden>
        ·
      </span>
      <span className="min-w-0 flex-1">{parsed.rest}</span>
    </p>
  )
}

function SessionCard({
  session,
  accentKey,
  showRecordingStatus,
  onSelect,
}: {
  session: SessionItem
  accentKey: AccentKey
  showRecordingStatus: boolean
  onSelect: (session: SessionItem) => void
}) {
  const accent = DAY_ACCENTS[accentKey]
  const typeLabel = (session.type ?? 'Type').toString().toUpperCase()

  return (
    <button
      type="button"
      className={cn(
        'w-full overflow-hidden rounded-md border border-secondary-gray-150 bg-white px-[18px] py-[14px] text-left shadow-light backdrop-blur-25 transition-colors',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary-blue-100',
      )}
      onClick={() => onSelect(session)}
      aria-haspopup="dialog">
      <p className={cn('mb-0.5 text-xs font-semibold uppercase', accent.meta)}>
        <span>{session.time ?? 'Time'}</span>
        <span className="mx-1" aria-hidden>
          •
        </span>
        <span>{typeLabel}</span>
      </p>
      <PrismicRichText
        field={session.session_title}
        components={{
          paragraph: ({ children }) => (
            <p className="text-lg font-semibold leading-[140%] tracking-[1%] text-primary-ink-100">{children}</p>
          ),
        }}
      />
      <div className="mt-2 flex flex-col gap-1">
        {sessionSpeakerLines(session.session_speakers_list).map((text, i) => (
          <SessionSpeakerLine key={i} text={text} />
        ))}
      </div>
      {prismic.isFilled.link(session.recording_link) ? (
        <span className="flex items-center gap-1.5 pt-2 font-halyardDisplay text-base font-medium leading-[140%] tracking-[0.01em] text-primary-ink-100">
          Watch recording
          <Image src="/images/angle-right-green.svg" width={9} height={9} alt="" aria-hidden="true" />
        </span>
      ) : showRecordingStatus ? (
        <span className="block pt-2 font-halyardDisplay text-base font-normal leading-[140%] tracking-[0.01em] text-primary-ink-60">
          In person only
        </span>
      ) : null}
    </button>
  )
}

function ModalSpeakerRow({
  line,
  matched,
  accentKey,
}: {
  line: string
  matched: SummitSpeakerDirectoryItem | undefined
  accentKey: AccentKey
}) {
  const parsed = splitSummitSessionSpeakerLine(line)
  const nameFromPrismic = matched ? prismic.asText(matched.name ?? []).trim() : ''
  const designationFromPrismic = matched?.designation?.toString().trim() ?? ''
  const showImage = matched && prismic.isFilled.image(matched.logo)
  const accent = DAY_ACCENTS[accentKey]

  return (
    <div className="flex items-center gap-4">
      <div className={cn('relative h-14 w-14 rounded-tr-xl border bg-white', accent.avatarBorder)}>
        {showImage ? (
          <PrismicNextImage
            field={matched!.logo}
            fill
            sizes="56"
            className="!left-1.5 !top-1.5 rounded-tr-xl object-cover object-left-top"
            alt=""
          />
        ) : null}
      </div>
      <div className="min-w-0 flex-1">
        {matched && (nameFromPrismic || designationFromPrismic) ? (
          <>
            {nameFromPrismic ? (
              <p className="text-sm font-semibold leading-[140%] text-primary-ink-100">{nameFromPrismic}</p>
            ) : null}
            {designationFromPrismic ? (
              <p className="mt-0.5 text-xs leading-[140%] text-primary-ink-60">{designationFromPrismic}</p>
            ) : null}
          </>
        ) : parsed ? (
          <>
            <p className="text-sm font-semibold leading-[140%] text-primary-ink-100">{parsed.name}</p>
            <p className="mt-0.5 text-xs leading-[140%] text-primary-ink-60">{parsed.rest}</p>
          </>
        ) : (
          <p className="text-sm leading-[140%] text-primary-ink-100">{normalizeSummitSpeakerLine(line)}</p>
        )}
      </div>
    </div>
  )
}

function SummitSessionDetailModal({
  open,
  onClose,
  session,
  accentKey,
  speakerDirectory,
}: {
  open: boolean
  onClose: () => void
  session: SessionItem | null
  accentKey: AccentKey
  speakerDirectory: SummitSpeakerDirectoryItem[]
}) {
  const titleId = useId()
  const closeRef = useRef<HTMLButtonElement>(null)
  const dialogPanelRef = useRef<HTMLDivElement>(null)
  const accent = DAY_ACCENTS[accentKey]

  useEffect(() => {
    if (!open || !session) return

    const prevFocusedEl = document.activeElement
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
        return
      }

      if (e.key !== 'Tab' || !dialogPanelRef.current) return

      const tabbables = getKeyboardFocusableElements(dialogPanelRef.current)
      if (tabbables.length === 0) return

      const active = document.activeElement as HTMLElement | null
      const activeIndex = tabbables.findIndex((el) => el === active || (active != null && el.contains(active)))
      if (activeIndex === -1) return

      if (e.shiftKey) {
        if (activeIndex === 0) {
          e.preventDefault()
          tabbables[tabbables.length - 1]?.focus()
        }
      } else if (activeIndex === tabbables.length - 1) {
        e.preventDefault()
        tabbables[0]?.focus()
      }
    }

    const handleFocusInCapture = (e: FocusEvent) => {
      const target = e.target as Node | null
      if (!dialogPanelRef.current || !target) return
      if (dialogPanelRef.current.contains(target)) return
      closeRef.current?.focus()
    }

    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('focusin', handleFocusInCapture, true)

    queueMicrotask(() => {
      closeRef.current?.focus()
    })

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('focusin', handleFocusInCapture, true)
      document.body.style.overflow = prevOverflow
      if (prevFocusedEl instanceof HTMLElement && typeof prevFocusedEl.focus === 'function') {
        prevFocusedEl.focus()
      }
    }
  }, [open, session, onClose])

  if (!open || !session) return null
  if (typeof document === 'undefined') return null

  const typeLabel = (session.type ?? 'Speakers').toString().toUpperCase()
  const titleField = prismic.isFilled.richText(session.modal_title) ? session.modal_title : session.session_title
  const subtitleField = session.modal_sub_title
  const descriptionField = session.modal_description
  const lines = sessionSpeakerLines(session.session_speakers_list)
  const embedUrl = getYouTubeEmbedUrl(session.recording_link)

  return createPortal(
    <div
      className="fixed inset-0 z-[99999] flex items-start justify-center overflow-y-auto bg-primary-ink-100/60 p-4 pt-16 sm:items-center sm:pt-4"
      onClick={onClose}
      role="presentation">
      <div
        ref={dialogPanelRef}
        className="relative my-auto w-full max-w-[640px] rounded-md border border-secondary-gray-150 bg-white p-9"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}>
        <button
          ref={closeRef}
          type="button"
          onClick={onClose}
          className="text-primary-ink-40 absolute right-4 top-4 rounded-md p-1 transition-colors hover:bg-secondary-gray-150 hover:text-primary-ink-100"
          aria-label="Close session details">
          <svg aria-hidden className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <p className={cn('mb-2 text-xs font-semibold uppercase tracking-wide', accent.meta)}>
          <span>{session.time}</span>
          {session.time && typeLabel ? (
            <span className="mx-1" aria-hidden>
              ·
            </span>
          ) : null}
          <span>{typeLabel}</span>
        </p>

        <div id={titleId}>
          <PrismicRichText
            field={titleField}
            components={{
              paragraph: ({ children }) => (
                <p className="text-2xl font-semibold leading-[120%] tracking-[0.01em] text-primary-ink-100">
                  {children}
                </p>
              ),
            }}
          />
        </div>

        {prismic.isFilled.richText(subtitleField) ? (
          <div className="mt-2">
            <PrismicRichText
              field={subtitleField}
              components={{
                paragraph: ({ children }) => (
                  <p className="font-halyardDisplay text-xl leading-[140%] text-primary-ink-85">{children}</p>
                ),
              }}
            />
          </div>
        ) : null}

        {prismic.isFilled.richText(descriptionField) ? (
          <div className="mt-8">
            <PrismicRichText
              field={descriptionField}
              components={{
                paragraph: ({ children }) => <p className="text-base text-primary-ink-85">{children}</p>,
              }}
            />
          </div>
        ) : null}

        {lines.length > 0 ? (
          <div className="mt-8">
            <p className={cn('mb-4 text-xs font-semibold uppercase tracking-wide', accent.meta)}>
              {(session.type === 'Panel' ? 'Panel' : 'Speaker').toString().toUpperCase()}
            </p>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {lines.map((line, i) => (
                <ModalSpeakerRow
                  key={i}
                  line={line}
                  matched={findSpeakerForSummitLine(line, speakerDirectory)}
                  accentKey={accentKey}
                />
              ))}
            </div>
          </div>
        ) : null}

        {embedUrl ? (
          <div className="relative mt-8 aspect-video w-full overflow-hidden rounded-md bg-primary-ink-30">
            <iframe
              src={embedUrl}
              title="Session recording"
              className="absolute inset-0 h-full w-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        ) : prismic.isFilled.link(session.recording_link) ? (
          <div className="mt-8">
            <PrismicLink field={session.recording_link} className="btn-interaction inline-flex w-fit items-center gap-1.5">
              <span>Watch recording</span>
              <Image src="/images/angle-right-green.svg" width={9} height={9} alt="" aria-hidden="true" />
            </PrismicLink>
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  )
}

type DataObject = Record<string, any>

type SummitSessionScheduleClientProps = {
  primary: DataObject
  sessions11: SessionItem[]
  sessions12: SessionItem[]
  sessions13: SessionItem[]
  speakerDirectory: SummitSpeakerDirectoryItem[]
  showRecordingStatus?: boolean
}

const DayColumn = ({
  accentKey,
  sessionTitleField,
  sessionDescription,
  sessions,
  showRecordingStatus,
  onSelectSession,
}: {
  accentKey: AccentKey
  sessionTitleField: DataObject['may_11_session_title']
  sessionDescription: string | undefined | null
  sessions: SessionItem[]
  showRecordingStatus: boolean
  onSelectSession: (session: SessionItem, accent: AccentKey) => void
}) => {
  const accent = DAY_ACCENTS[accentKey]

  return (
    <div className="flex min-w-0 flex-col gap-6">
      <div className="flex flex-col">
        <PrismicRichText
          field={sessionTitleField}
          components={{
            heading4: ({ children }) => <h4 className="text-lg">{children}</h4>,
          }}
        />
        {sessionDescription ? (
          <p className="mt-1 text-sm leading-[140%] text-primary-ink-60">{sessionDescription}</p>
        ) : null}
        <div className={cn('mt-[14px] h-1 w-full rounded-sm', accent.bar)} aria-hidden />
      </div>
      <div className="flex flex-col gap-4">
        {sessions.map((session: SessionItem, index: number) => (
          <SessionCard
            key={`${accentKey}-${index}`}
            session={session}
            accentKey={accentKey}
            showRecordingStatus={showRecordingStatus}
            onSelect={(s) => onSelectSession(s, accentKey)}
          />
        ))}
      </div>
    </div>
  )
}

export default function SummitSessionScheduleClient({
  primary,
  sessions11,
  sessions12,
  sessions13,
  speakerDirectory,
  showRecordingStatus = false,
}: SummitSessionScheduleClientProps) {
  const [modalSession, setModalSession] = useState<SessionItem | null>(null)
  const [modalAccent, setModalAccent] = useState<AccentKey>('may11')

  const closeModal = useCallback(() => setModalSession(null), [])

  const openSession = useCallback((session: SessionItem, accent: AccentKey) => {
    setModalAccent(accent)
    setModalSession(session)
  }, [])

  return (
    <>
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <DayColumn
          accentKey="may11"
          sessionTitleField={primary.may_11_session_title}
          sessionDescription={primary.may_11_session_description}
          sessions={sessions11}
          showRecordingStatus={showRecordingStatus}
          onSelectSession={openSession}
        />
        <DayColumn
          accentKey="may12"
          sessionTitleField={primary.may_12_session_title}
          sessionDescription={primary.may_12_session_description}
          sessions={sessions12}
          showRecordingStatus={showRecordingStatus}
          onSelectSession={openSession}
        />
        <DayColumn
          accentKey="may13"
          sessionTitleField={primary.may_13_session_title}
          sessionDescription={primary.may_13_session_description}
          sessions={sessions13}
          showRecordingStatus={showRecordingStatus}
          onSelectSession={openSession}
        />
      </div>

      <SummitSessionDetailModal
        open={modalSession !== null}
        onClose={closeModal}
        session={modalSession}
        accentKey={modalAccent}
        speakerDirectory={speakerDirectory}
      />
    </>
  )
}
